import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Idea } from '@frontend/data-access';
import { UnsplashImage } from '@frontend/shared/ui';
import { Image, Typography } from 'antd';
import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import getConfig from 'next/config';

const { Title } = Typography;

interface IdeaDetailBodyProps {
  idea: Idea;
  size?: 'small' | 'medium';
  collapsed?: boolean;
  showCollapse?: boolean;
}

function IdeaDetailBody({
  idea,
  size = 'medium',
  collapsed = false,
  showCollapse = true,
}: IdeaDetailBodyProps) {
  const { publicRuntimeConfig } = getConfig();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const { t } = useTranslation('common');

  function countWords(str: string) {
    if (str) return str.trim().split(/\s+/).length;
    else return 0;
  }

  const imageSize = size === 'small' ? 100 : 180;

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  return (
    <div className="space-y-6">
      <div className="overflow-hidden">
        <div className="float-left">
          {idea?.coverPhoto && (
            <div
              className={`md:min-w-[${imageSize}px] max-w-[${imageSize}px] mr-3`}
            >
              <div className="flex flex-col">
                <Image
                  className="rounded-xl overflow-hidden"
                  width={imageSize}
                  height={imageSize}
                  alt={idea.name}
                  preview={false}
                  src={idea?.coverPhoto.url}
                />
              </div>
            </div>
          )}
          {idea?.coverPhotoUrl && (
            <UnsplashImage
              image={idea.coverPhotoUrl}
              name={idea.name}
              size={size}
            />
          )}
          {!idea?.coverPhoto && !idea?.coverPhotoUrl && (
            <Image
              className="rounded-xl overflow-hidden"
              alt={idea.name}
              width={180}
              height={180}
              preview={false}
              src={`${publicRuntimeConfig.staticFolder}/data/idea_placeholder_small.jpeg`}
            />
          )}
        </div>
        <div className="contents text-sm">
          {size === 'medium' && <Title level={4}>{idea.name}</Title>}
          <div
            className={`text-wrap break-words whitespace-break-spaces ${
              isCollapsed ? 'line-clamp-[8]' : ''
            }`}
          >
            {idea.problem}
          </div>
          <div>
            {showCollapse && countWords(idea.problem) > 90 && (
              <span
                className="text-primary-600 cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <>
                    <DownOutlined /> {t('see_more')}
                  </>
                ) : (
                  <>
                    <UpOutlined /> {t('hide')}
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IdeaDetailBody;
